import * as Sentry from "@sentry/browser";

/**
 * Sentry reporting for failed API calls (DE-9501).
 *
 * Capture sites must pass a real `Error` so Sentry renders a stack trace,
 * and must fingerprint per endpoint + status so failures from different
 * endpoints do not collapse into one unactionable issue.
 */

const REDACTED = "[redacted]";
const UNKNOWN_PATH = "/unknown";

/** Statuses handled by the auth interceptor, not reported as errors. */
const UNAUTHORIZED = 401;

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Personal data that must never reach Sentry breadcrumbs. */
const PII_KEYS = new Set([
  "contactemail",
  "contactname",
  "emailaddress",
  "email",
  "username",
  "firstname",
  "lastname",
  "phone",
  "telephone",
  "mobile",
]);

/** Credential-bearing keys, matched anywhere in the key name. */
const CREDENTIAL_KEY_PATTERN =
  /password|token|secret|authorization|bearer|apikey/i;

export interface ApiFailureReport {
  /** API helper that made the call, e.g. `postNewRequest`. */
  helper: string;
  method: string;
  url?: string | null;
  status?: number | null;
}

export interface ApiErrorResponseSummary {
  status?: number | null;
  statusText?: string | null;
  data?: unknown;
  url: string;
  method?: string | null;
}

const isSensitiveKey = (key: string): boolean =>
  PII_KEYS.has(key.toLowerCase()) || CREDENTIAL_KEY_PATTERN.test(key);

/**
 * Values we must not serialise: uploads have no useful JSON form and can be
 * large enough to push the event past Sentry's payload limit.
 */
const describeOpaqueValue = (value: unknown): string | null => {
  if (typeof FormData !== "undefined" && value instanceof FormData) {
    return "[FormData]";
  }
  if (typeof File !== "undefined" && value instanceof File) {
    return "[File]";
  }
  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return "[Blob]";
  }
  return null;
};

/**
 * Deep-copy `value`, replacing personal and credential fields with
 * `[redacted]`. Business identifiers (part number, serial number, aircraft
 * registration, customer PO, pricing) are deliberately kept so failures
 * stay debuggable.
 */
export const redactSensitiveValues = (value: unknown): unknown => {
  const seen = new WeakSet<object>();

  const walk = (input: unknown): unknown => {
    const opaque = describeOpaqueValue(input);
    if (opaque) {
      return opaque;
    }
    if (input === null || typeof input !== "object") {
      return input;
    }
    if (input instanceof Date) {
      return input.toISOString();
    }
    if (seen.has(input)) {
      return "[Circular]";
    }
    seen.add(input);

    if (Array.isArray(input)) {
      return input.map(walk);
    }

    const entries = Object.entries(input as Record<string, unknown>);
    return entries.reduce<Record<string, unknown>>((acc, [key, val]) => {
      acc[key] = isSensitiveKey(key) ? REDACTED : walk(val);
      return acc;
    }, {});
  };

  return walk(value);
};

/**
 * Strip the origin and any record identifiers so one endpoint maps to one
 * Sentry issue regardless of environment host or which record failed.
 */
export const normaliseApiPath = (url?: string | null): string => {
  if (!url || typeof url !== "string") {
    return UNKNOWN_PATH;
  }

  const withoutQuery = url.split(/[?#]/)[0];
  const withoutOrigin = withoutQuery.replace(
    /^[a-z][a-z0-9+.-]*:\/\/[^/]+/i,
    "",
  );
  const path = withoutOrigin.startsWith("/")
    ? withoutOrigin
    : `/${withoutOrigin}`;

  const normalised = path
    .split("/")
    .map((segment) => {
      if (!segment) {
        return segment;
      }
      if (/^\d+$/.test(segment) || GUID_PATTERN.test(segment)) {
        return ":id";
      }
      return segment;
    })
    .join("/")
    .replace(/(.)\/$/, "$1");

  return normalised === "/" ? UNKNOWN_PATH : normalised;
};

/**
 * Slim, redacted view of an axios error response. Avoids serialising the
 * whole response (which carries the request object and auth headers).
 */
export const summariseApiErrorResponse = (
  error: unknown,
): ApiErrorResponseSummary => {
  const response = (error as { response?: Record<string, unknown> })
    ?.response;
  const config = response?.config as
    | { url?: string; method?: string }
    | undefined;

  return {
    status: (response?.status as number) ?? null,
    statusText: (response?.statusText as string) ?? null,
    data: redactSensitiveValues(response?.data),
    url: normaliseApiPath(config?.url),
    method: config?.method ? config.method.toUpperCase() : null,
  };
};

/** 401s are retried/handled by the auth layer, so they are not reported. */
export const shouldReportApiFailure = (status?: number | null): boolean =>
  Boolean(status) && status !== UNAUTHORIZED;

/**
 * Report a failed API call, grouped per helper + method + endpoint + status.
 * Returns whether an event was sent so callers can branch in tests.
 */
export const reportApiFailureToSentry = ({
  helper,
  method,
  url,
  status,
}: ApiFailureReport): boolean => {
  if (!shouldReportApiFailure(status)) {
    return false;
  }

  const apiPath = normaliseApiPath(url);
  const httpMethod = method.toUpperCase();
  const httpStatus = String(status);

  Sentry.withScope((scope) => {
    scope.setFingerprint([helper, httpMethod, apiPath, httpStatus]);
    scope.setTags({
      apiHelper: helper,
      apiPath,
      httpMethod,
      httpStatus,
    });
    Sentry.captureException(
      new Error(`${httpMethod} ${apiPath} failed (${httpStatus})`),
    );
  });

  return true;
};
